/**
 * 首页「个人主页」配置的运行时工具：默认值合并、链接类型元数据、URL 白名单、前台过滤。
 *
 * 两条硬规则：
 *  1. 任何从后台/JSON 进来的值都不可信 —— 一律先过 normalizeProfile()（补全 + 截断 + 协议白名单）。
 *  2. 前台只拿得到「启用且确实有内容」的链接 —— 见 publicProfile()，关掉的链接不下发。
 */
import {
  DEFAULT_PRICE_LIST,
  DEFAULT_PROFILE,
  toSiteGame,
  type PriceCategory,
  type PriceItem,
  type SiteGame,
  type SiteImage,
  type SiteLink,
  type SiteLinkKind,
  type SitePriceList,
  type SiteProfile,
  type SiteTribute,
  type SiteWork,
} from "./types.ts";

/** 链接类型元数据：后台选择器 + 前台图标都用这一份（颜色是品牌色，不是硬编码文案） */
export interface LinkKindMeta {
  /** 中文名，后台下拉里显示 */
  label: string;
  /** 图标里的字符（X / QQ / 微 …）；x 与 telegram 前台会换成真 SVG */
  monogram: string;
  /** 品牌主色，卡片图标底色 */
  color: string;
  /** 后台新建时的 label 默认值 */
  defaultLabel: string;
  /** 后台新建时的 sublabel 默认值 */
  defaultSublabel: string;
  /** 后台 URL 输入框的 placeholder */
  urlPlaceholder: string;
}

export const LINK_KINDS: Record<SiteLinkKind, LinkKindMeta> = {
  x: {
    label: "推特 / X",
    monogram: "X",
    color: "#14171f",
    defaultLabel: "官方推特 / X",
    defaultSublabel: "点击直达 @你的账号",
    urlPlaceholder: "https://x.com/你的账号",
  },
  telegram: {
    label: "Telegram",
    monogram: "✈",
    color: "#229ed9",
    defaultLabel: "Telegram 群",
    defaultSublabel: "点击直达 t.me/你的群",
    urlPlaceholder: "https://t.me/你的群",
  },
  qq: {
    label: "QQ 群",
    monogram: "QQ",
    color: "#12b7f5",
    defaultLabel: "QQ 群",
    defaultSublabel: "群号 123456，点击复制",
    urlPlaceholder: "https://qm.qq.com/q/xxxx（也可以留空只填群号）",
  },
  discord: {
    label: "Discord",
    monogram: "DC",
    color: "#5865f2",
    defaultLabel: "Discord 服务器",
    defaultSublabel: "点击直达邀请链接",
    urlPlaceholder: "https://discord.gg/xxxx",
  },
  bilibili: {
    label: "哔哩哔哩",
    monogram: "B",
    color: "#fb7299",
    defaultLabel: "哔哩哔哩",
    defaultSublabel: "点击直达空间",
    urlPlaceholder: "https://space.bilibili.com/xxxx",
  },
  weibo: {
    label: "微博",
    monogram: "微",
    color: "#e6162d",
    defaultLabel: "微博",
    defaultSublabel: "点击直达主页",
    urlPlaceholder: "https://weibo.com/u/xxxx",
  },
  youtube: {
    label: "YouTube",
    monogram: "▶",
    color: "#ff0033",
    defaultLabel: "YouTube 频道",
    defaultSublabel: "点击直达频道",
    urlPlaceholder: "https://youtube.com/@xxxx",
  },
  email: {
    label: "邮箱",
    monogram: "@",
    color: "#8b6bd6",
    defaultLabel: "联系邮箱",
    defaultSublabel: "点击发邮件",
    urlPlaceholder: "mailto:hi@example.com",
  },
  custom: {
    label: "自定义链接",
    monogram: "🔗",
    color: "#a855f7",
    defaultLabel: "自定义链接",
    defaultSublabel: "点击直达",
    urlPlaceholder: "https://example.com",
  },
};

/** 后台选择器里的展示顺序 */
export const LINK_KIND_ORDER: SiteLinkKind[] = [
  "x",
  "telegram",
  "qq",
  "discord",
  "bilibili",
  "weibo",
  "youtube",
  "email",
  "custom",
];

/** 只有这些协议的链接才允许渲染成可点击的 <a>（防 javascript: / data: 之类的注入） */
const SAFE_LINK_PROTOCOL = /^(https?:\/\/|mailto:|tel:)/i;

/** 头像：允许图片外链或站点内相对路径（如 /covers/x.svg），其它一律丢弃 */
const SAFE_IMAGE_URL = /^(\/[^/]|https?:\/\/)/i;

/** 主题色 / 渐变色：只接受 #rgb / #rrggbb —— 这些值会写进 style，绝不能让任意 CSS 进来 */
const SAFE_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isSiteLinkKind(value: unknown): value is SiteLinkKind {
  return typeof value === "string" && (LINK_KIND_ORDER as string[]).includes(value);
}

/** 链接地址白名单：不合法直接返回空串（宁可不显示，也不渲染危险协议） */
export function sanitizeUrl(raw: unknown, maxLength = 300): string {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return "";
  return SAFE_LINK_PROTOCOL.test(value) ? value.slice(0, maxLength) : "";
}

export function sanitizeImageUrl(raw: unknown): string {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return "";
  return SAFE_IMAGE_URL.test(value) ? value.slice(0, 300) : "";
}

/** 颜色白名单：不合法直接用 fallback（默认主题色），永远不会把空串写进 style */
export function sanitizeColor(raw: unknown, fallback: string): string {
  const value = typeof raw === "string" ? raw.trim() : "";
  return SAFE_COLOR.test(value) ? value.toLowerCase() : fallback;
}

/** @账号：运营方可能顺手把 @ 也敲进去，统一去掉，前台自己加前缀 */
function readHandle(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.trim().replace(/^@+/, "").slice(0, 40);
}

function readString(raw: unknown, fallback: string, maxLength: number): string {
  if (typeof raw !== "string") return fallback;
  const value = raw.trim();
  return value ? value.slice(0, maxLength) : fallback;
}

/** 允许留空的字符串（和 readString 的区别：空串就是空串，不回落默认值） */
function readOptionalString(raw: unknown, maxLength: number): string {
  return typeof raw === "string" ? raw.trim().slice(0, maxLength) : "";
}

function readBool(raw: unknown, fallback: boolean): boolean {
  return typeof raw === "boolean" ? raw : fallback;
}

function readStringArray(raw: unknown, max: number, maxLength: number): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const value = item.trim().replace(/^#+/, "").slice(0, maxLength);
    if (!value || out.includes(value)) continue;
    out.push(value);
    if (out.length >= max) break;
  }
  return out;
}

/** 单张链接卡片：补全字段 + URL 白名单；保留"还没填地址"的半成品（后台草稿需要） */
export function normalizeLink(input: unknown, index = 0): SiteLink {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  const kind = isSiteLinkKind(raw.kind) ? raw.kind : "custom";
  const meta = LINK_KINDS[kind];
  return {
    id: readString(raw.id, `link-${index + 1}`, 64),
    kind,
    label: readString(raw.label, meta.defaultLabel, 60),
    sublabel: readOptionalString(raw.sublabel, 120),
    url: sanitizeUrl(raw.url),
    copyText: readOptionalString(raw.copyText, 120),
    enabled: readBool(raw.enabled, true),
  };
}

export function normalizeLinks(raw: unknown): SiteLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 24).map((item, index) => normalizeLink(item, index));
}

/** 资料面板里的一条作品：标题必填（没填就给个序号占位），网址走同一套白名单 */
export function normalizeWork(input: unknown, index = 0): SiteWork {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  return {
    id: readString(raw.id, `work-${index + 1}`, 64),
    title: readString(raw.title, `作品 ${index + 1}`, 60),
    sublabel: readOptionalString(raw.sublabel, 120),
    url: sanitizeUrl(raw.url),
  };
}

export function normalizeWorks(raw: unknown): SiteWork[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 12).map((item, index) => normalizeWork(item, index));
}

export function normalizeTribute(raw: unknown): SiteTribute {
  const fallback = DEFAULT_PROFILE.tribute;
  const input = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    enabled: readBool(input.enabled, fallback.enabled),
    label: readString(input.label, fallback.label, 60),
    // 「没配过」→ 默认文案；「显式清空」（""）→ 保持空，尊重运营方的清空操作
    sublabel: input.sublabel === undefined ? fallback.sublabel : readOptionalString(input.sublabel, 120),
    soonEnabled: readBool(input.soonEnabled, fallback.soonEnabled),
    soonLabel: readString(input.soonLabel, fallback.soonLabel, 60),
    soonText: input.soonText === undefined ? fallback.soonText : readOptionalString(input.soonText, 120),
  };
}

/* --------------------------------- 图片 / 价格表 / 游戏 --------------------------------- */

/** 一张站点自有图片（壁纸轮换用）：URL 走同一套图片白名单，乱填就丢掉 */
export function normalizeImage(input: unknown, index = 0): SiteImage {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  return {
    id: readString(raw.id, `img-${index + 1}`, 64),
    url: sanitizeImageUrl(raw.url),
    enabled: readBool(raw.enabled, true),
  };
}

export function normalizeImages(raw: unknown, max = 12): SiteImage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .slice(0, max)
    .map((item, index) => normalizeImage(item, index))
    .filter((image) => image.url !== "");
}

let imageSeq = 0;

/** 后台「添加壁纸」用：url 由上传或手填决定，先给个空壳 */
export function newSiteImage(url = "", existing: SiteImage[] = []): SiteImage {
  imageSeq += 1;
  let id = `img${Date.now().toString(36)}${imageSeq.toString(36)}`;
  while (existing.some((image) => image.id === id)) {
    imageSeq += 1;
    id = `img${Date.now().toString(36)}${imageSeq.toString(36)}`;
  }
  return { id, url: sanitizeImageUrl(url), enabled: true };
}

export function normalizePriceCategory(input: unknown, index = 0): PriceCategory {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  return {
    id: readString(raw.id, `cat-${index + 1}`, 64),
    name: readString(raw.name, `分类 ${index + 1}`, 30),
  };
}

export function normalizePriceCategories(raw: unknown): PriceCategory[] {
  if (!Array.isArray(raw)) return [];
  const out: PriceCategory[] = [];
  for (const [index, item] of raw.slice(0, 20).entries()) {
    const category = normalizePriceCategory(item, index);
    if (out.some((existing) => existing.id === category.id)) continue;
    out.push(category);
  }
  return out;
}

/**
 * 价格条目：
 * - 价格/单位允许留空（「面议」也合法），名称必填（没填给序号占位，和作品一致）；
 * - categoryId 找不到对应分类时保留原值，前台会归到「未分类」（后台提示运营修正）。
 */
export function normalizePriceItem(input: unknown, index = 0): PriceItem {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  return {
    id: readString(raw.id, `price-${index + 1}`, 64),
    categoryId: readOptionalString(raw.categoryId, 64),
    // 名称刻意不补占位值：后台「加了条目还没填名字」的半成品不该出现在前台（visiblePriceItems 会滤掉空名）
    name: readOptionalString(raw.name, 60),
    price: readOptionalString(raw.price, 40),
    unit: readOptionalString(raw.unit, 16),
    description: readOptionalString(raw.description, 500),
    enabled: readBool(raw.enabled, true),
  };
}

export function normalizePriceItems(raw: unknown): PriceItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 200).map((item, index) => normalizePriceItem(item, index));
}

export function normalizePriceList(raw: unknown): SitePriceList {
  const fallback = DEFAULT_PRICE_LIST;
  const input = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    enabled: readBool(input.enabled, fallback.enabled),
    closed: readBool(input.closed, fallback.closed),
    closedText: readString(input.closedText, fallback.closedText, 60),
    // 「没配过」→ 默认文案；「显式清空」→ 保持空（尊重运营的清空操作），和 tribute 一样的规矩
    note: input.note === undefined ? fallback.note : readOptionalString(input.note, 300),
    contactNote: input.contactNote === undefined ? fallback.contactNote : readOptionalString(input.contactNote, 300),
    categories: normalizePriceCategories(input.categories),
    items: normalizePriceItems(input.items),
  };
}

let priceSeq = 0;

function nextId(prefix: string, existing: { id: string }[]): string {
  priceSeq += 1;
  let id = `${prefix}${Date.now().toString(36)}${priceSeq.toString(36)}`;
  while (existing.some((item) => item.id === id)) {
    priceSeq += 1;
    id = `${prefix}${Date.now().toString(36)}${priceSeq.toString(36)}`;
  }
  return id;
}

export function newPriceCategory(existing: PriceCategory[] = []): PriceCategory {
  return { id: nextId("c", existing), name: "" };
}

export function newPriceItem(categoryId: string, existing: PriceItem[] = []): PriceItem {
  return { id: nextId("p", existing), categoryId, name: "", price: "", unit: "", description: "", enabled: true };
}

/** 出厂默认（games.json）也用同一套归一化，保证「后台清空」和「从没配过」走同一条路 */
export function normalizeGames(raw: unknown): SiteGame[] {
  if (!Array.isArray(raw)) return [];
  const out: SiteGame[] = [];
  for (const [index, item] of raw.slice(0, 30).entries()) {
    const game = toSiteGame(item, index);
    if (out.some((existing) => existing.id === game.id)) continue;
    out.push(game);
  }
  return out;
}

export function newSiteGame(existing: SiteGame[] = []): SiteGame {
  return {
    id: nextId("g", existing),
    name: "",
    summary: "",
    cover: "",
    playersMin: 1,
    playersMax: 2,
    adult: false,
    kind: "internal",
    route: "",
    status: "ready",
    enabled: true,
  };
}

/** 前台实际会用到的壁纸 URL 列表（按顺序）：启用中的壁纸；一张都没有就退回 backgroundUrl */
export function effectiveWallpapers(profile: SiteProfile): string[] {
  const list = profile.wallpapers.filter((image) => image.enabled && image.url !== "").map((image) => image.url);
  if (list.length > 0) return list;
  return profile.backgroundUrl ? [profile.backgroundUrl] : [];
}

/** 前台真正会显示的价格条目：启用中 + 有名字（分类缺失的归「未分类」由调用方处理） */
export function visiblePriceItems(list: SitePriceList): PriceItem[] {
  return list.items.filter((item) => item.enabled && item.name.trim() !== "");
}

/** 价格表这一块在前台要不要出现 */
export function priceListVisible(list: SitePriceList): boolean {
  return list.enabled && visiblePriceItems(list).length > 0;
}

/**
 * 把任意来源（D1 里的旧数据 / 后台 PATCH / 导入的 JSON）归一化成可用的 SiteProfile。
 *
 * 关键是区分两种「空」：
 *  - 字段**不存在**（老库没这个字段 / 全新装）→ 回落 DEFAULT_PROFILE 的样例，
 *    这样线上老库不重新种子也能有一个像样的首页；
 *  - 字段**是空数组/空串**（运营方在后台主动清空）→ 原样保留，不要把样例又塞回去。
 */
export function normalizeProfile(raw: unknown): SiteProfile {
  const input = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    siteName: readString(input.siteName, DEFAULT_PROFILE.siteName, 60),
    handle: readHandle(input.handle),
    avatarUrl: sanitizeImageUrl(input.avatarUrl),
    // 签名和简介一样：没配过 → 默认文案；显式清空 → 保持空
    signature: input.signature === undefined ? DEFAULT_PROFILE.signature : readOptionalString(input.signature, 200),
    description: input.description === undefined ? DEFAULT_PROFILE.description : readOptionalString(input.description, 600),
    illustrationUrl: sanitizeImageUrl(input.illustrationUrl),
    backgroundUrl: sanitizeImageUrl(input.backgroundUrl),
    wallpapers: input.wallpapers === undefined ? [] : normalizeImages(input.wallpapers),
    wallpaperRotate: readBool(input.wallpaperRotate, false),
    backgroundFrom: sanitizeColor(input.backgroundFrom, DEFAULT_PROFILE.backgroundFrom),
    backgroundTo: sanitizeColor(input.backgroundTo, DEFAULT_PROFILE.backgroundTo),
    accentColor: sanitizeColor(input.accentColor, DEFAULT_PROFILE.accentColor),
    tagline: input.tagline === undefined ? [...DEFAULT_PROFILE.tagline] : readStringArray(input.tagline, 8, 200),
    tags: input.tags === undefined ? [...DEFAULT_PROFILE.tags] : readStringArray(input.tags, 12, 24),
    links: input.links === undefined ? DEFAULT_PROFILE.links.map((link) => ({ ...link })) : normalizeLinks(input.links),
    works: input.works === undefined ? [] : normalizeWorks(input.works),
    tribute: normalizeTribute(input.tribute),
    priceList: normalizePriceList(input.priceList),
  };
}

/**
 * 下发前台的版本：只留「启用且确实有内容」的东西。
 * - links：关掉的、既没网址也没可复制内容的，不下发；
 * - wallpapers：关掉的 / URL 被白名单清空的，不下发（不参与轮换）；
 * - priceList：关掉的价格表整个清空，条目同样只留启用中的。
 */
export function publicProfile(profile: SiteProfile): SiteProfile {
  return {
    ...profile,
    links: profile.links.filter((link) => link.enabled && (link.url !== "" || link.copyText !== "")),
    wallpapers: profile.wallpapers.filter((image) => image.enabled && image.url !== ""),
    priceList: profile.priceList.enabled
      ? { ...profile.priceList, items: visiblePriceItems(profile.priceList) }
      : { ...DEFAULT_PRICE_LIST, categories: [], items: [] },
  };
}

/** 前台拿到的链接能不能点（能点才渲染 <a>，否则渲染不可点的卡片） */
export function linkHref(link: SiteLink): string {
  return link.copyText ? "" : sanitizeUrl(link.url);
}

let linkSeq = 0;

/** 后台「添加链接」用：生成一个新卡片（id 只要在配置里唯一即可） */
export function newSiteLink(kind: SiteLinkKind, existing: SiteLink[] = []): SiteLink {
  linkSeq += 1;
  let id = `l${Date.now().toString(36)}${linkSeq.toString(36)}`;
  while (existing.some((link) => link.id === id)) {
    linkSeq += 1;
    id = `l${Date.now().toString(36)}${linkSeq.toString(36)}`;
  }
  const meta = LINK_KINDS[kind];
  return {
    id,
    kind,
    label: meta.defaultLabel,
    sublabel: meta.defaultSublabel,
    url: "",
    copyText: "",
    enabled: true,
  };
}

let workSeq = 0;

/** 后台「添加作品」用：生成一条空作品（标题给占位，保存前运营会改） */
export function newSiteWork(existing: SiteWork[] = []): SiteWork {
  workSeq += 1;
  let id = `w${Date.now().toString(36)}${workSeq.toString(36)}`;
  while (existing.some((work) => work.id === id)) {
    workSeq += 1;
    id = `w${Date.now().toString(36)}${workSeq.toString(36)}`;
  }
  return { id, title: "", sublabel: "", url: "" };
}

/** 数组里上移/下移一项（后台排序用，返回新数组） */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return list;
  next.splice(to, 0, item);
  return next;
}
